import { Bell, CheckCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, type Notification } from '@/lib/api-client';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

const POLL_INTERVAL_MS = 30_000;

export function NotificationsPopover() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const countQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.notificationsUnreadCount(),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 10_000,
  });

  const listQuery = useQuery({
    queryKey: ['notifications', 'list', 'unread-preview'],
    queryFn: () => apiClient.notificationsList(true, 10, 0),
    enabled: open,
    staleTime: 5_000,
  });

  const markAsReadMut = useMutation({
    mutationFn: (id: string) => apiClient.notificationsMarkAsRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMut = useMutation({
    mutationFn: () => apiClient.notificationsMarkAllAsRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const count = countQuery.data?.count ?? 0;
  const items = listQuery.data?.items ?? [];

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleItemClick(item: Notification) {
    markAsReadMut.mutate(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  }

  function handleSeeAll() {
    setOpen(false);
    navigate('/escritorio/notificacoes');
  }

  const badgeText = count >= 10 ? '9+' : String(count);

  return (
    <div ref={containerRef} className="relative">
        <button
          type="button"
          data-tour="topbar-notifications"
          aria-label="Notificações"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'relative inline-flex h-9 w-9 items-center justify-center rounded-md transition',
            'text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]',
          )}
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-[var(--accent)] px-1 text-[10px] font-semibold text-[var(--accent-fg)]">
              {badgeText}
            </span>
          )}
        </button>

        {open && (
          <div
            role="dialog"
            className={cn(
              'absolute right-0 top-full z-50 mt-2 w-[22rem] overflow-hidden rounded-md border shadow-lg',
              'border-[var(--popover-border,var(--card-border))] bg-[var(--popover-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
            )}
          >
            <div className="flex items-center justify-between gap-2 border-b border-[var(--border)] px-4 py-3">
              <h3 className="text-sm font-medium text-[var(--fg)]">
                Notificações
              </h3>
              <button
                type="button"
                onClick={() => markAllAsReadMut.mutate()}
                disabled={
                  count === 0 ||
                  markAllAsReadMut.isPending ||
                  countQuery.isError
                }
                className={cn(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium transition',
                  'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                )}
                title="Marcar todas como lidas"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar todas
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {listQuery.isLoading ? (
                <p className="px-4 py-8 text-center text-xs text-[var(--fg-muted)]">
                  Carregando...
                </p>
              ) : listQuery.isError ? (
                <p className="px-4 py-8 text-center text-xs text-[var(--fg-muted)]">
                  Não foi possível carregar.
                </p>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="mx-auto h-6 w-6 text-[var(--fg-muted)]/40" />
                  <p className="mt-2 text-xs text-[var(--fg-muted)]">
                    Você está em dia. Sem novas notificações.
                  </p>
                </div>
              ) : (
                <ul className="flex flex-col">
                  {items.map((n) => (
                    <li
                      key={n.id}
                      className="border-b border-[var(--border)] last:border-b-0"
                    >
                      <button
                        type="button"
                        onClick={() => handleItemClick(n)}
                        className={cn(
                          'flex w-full flex-col gap-1 px-4 py-3 text-left transition',
                          'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]',
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-[var(--fg)]">
                              {n.title}
                            </p>
                            <p className="mt-0.5 line-clamp-2 text-xs text-[var(--fg-muted)]">
                              {n.body}
                            </p>
                            <p className="mt-1 text-[10px] uppercase tracking-wide text-[var(--fg-muted)]/70">
                              {formatRelativeTime(n.createdAt)}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-[var(--border)]">
              <button
                type="button"
                onClick={handleSeeAll}
                className="w-full px-4 py-2.5 text-center text-xs font-medium text-[var(--accent)] transition hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
              >
                Ver todas →
              </button>
            </div>
          </div>
        )}
      </div>
  );
}
